<template>
  <ion-content class="text-selectable modal-content">
    <div class="content-wrapper">
      <div class="scrollable-content">
        <div class="info-section">
          <div class="company-info">
            <div class="company-logo">
              <img v-if="company.image" :src="company.image" :alt="company.title" />
              <div v-else class="company-logo-placeholder">
                <font-awesome-icon :icon="['fas', 'building']" />
              </div>
            </div>
            <div class="company-details">
              <h2 class="company-name">{{ company.title }}</h2>
              <div class="company-subtitle">Brand</div>
            </div>
          </div>
          
          <div class="social-info">
            <div class="social-logo">
              <img :src="social.image" :alt="social.title" />
            </div>
            <div class="social-details">
              <h3 class="social-name">{{ social.title }}</h3>
              <div class="social-subtitle">Social Account</div>
            </div>
          </div>
        </div>

        <div class="status-section" v-if="actionInfo">
          <div class="status-card" :class="'status-card--' + actionInfo.color">           
            <div class="status-content">
              <div class="status-title">{{ actionInfo.text }} <span>{{ date }}</span></div>
              <div class="status-subtitle">{{ action }}</div>
            </div>            
          </div>
        </div>

        <div class="instructions-section" v-if="contentTitle || instruction">
          <div class="instructions-card">
            <div v-if="contentTitle" class="content-title">
              <h4>Content Title</h4>
              <p>{{ contentTitle }}</p>
            </div>
            <div v-if="instruction" class="instructions-content">
              <h4>{{ t("common.instructions") }}</h4>
              <div class="instructions-text" v-html="sanitizedInstruction"></div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="action-section">
        <ion-button expand="block" size="large" class="primary-action" @click="goToDeal">
          <font-awesome-icon :icon="['fas', 'external-link']" class="button-icon" />
          {{ t("common.go-to-deal") }}
        </ion-button>
      </div>
    </div>
  </ion-content>
</template>

<script lang="ts">
import {
  IonContent,
  modalController,
  IonButton
} from "@ionic/vue";
import { useRouter } from 'vue-router';
import { defineComponent, PropType, computed } from "vue";
import { DateTime } from "luxon";
import { calendarEventActionInfo, getSocialMediaIcon } from "@/utils";
import { CalendarEvent } from "@/types/talent";
import { SocialMedia } from "@/types";
import { useI18n } from "vue-i18n";
import localeService from "@/services/locale.service";



export default defineComponent({

  name: "DeliverableModal",
  components: { IonContent, IonButton },
  props: {
    calendarEvent: {
      type: Object as PropType<CalendarEvent>,
      required: true,
    },
  },
 /*  computed: {
    sanitizedInstruction() {
      // Basic URL regex pattern
      const urlPattern = /(https?:\/\/[^\s]+)/g;
      return this.instruction.replace(urlPattern, '<a href="$1" target="_blank">$1</a>') || ""
    }
  }, */
  setup(props) {
    const { t } = useI18n();
    const router = useRouter();

    const sanitizedInstruction = computed((): string => {
      const instruction = props.calendarEvent.deliverable?.talentInstructions || '';
      // Basic URL regex pattern
      const urlPattern = /(https?:\/\/[^\s]+)/g;
      return instruction.replace(urlPattern, '<a href="$1" target="_blank">$1</a>');
    });

    const goToDeal = () => {
      const dealId = props.calendarEvent.deliverable?.deal?.['@id'];
      if (dealId) {
        modalController.dismiss();
        router.push(`/tabs/deals/details/${dealId.split('/')[2]}`);
      }
    };
    return {
      t,
      sanitizedInstruction,
      actionInfo: calendarEventActionInfo(props.calendarEvent),
      company: {
        title: props.calendarEvent.deliverable.deal?.brand?.name || "-",
        image:
          props.calendarEvent.deliverable.deal?.brand?.mediaLogo
            ?.readPublicLink,
      },
      social: {
        title: props.calendarEvent.deliverable.talentSocialNetwork?.username || "-",
        image: getSocialMediaIcon(
          props.calendarEvent.deliverable.talentSocialNetwork?.socialNetwork?.name ||
          props.calendarEvent.deliverable.deliverableType?.socialNetwork?.name ||
          SocialMedia.generic
        ),
      },
      date: t("common.due-at", {
        date: DateTime.fromISO(props.calendarEvent.endDate)
          .setLocale(localeService.selectedLocale)
          .toLocaleString(DateTime.DATE_FULL),
      }),
      action: props.calendarEvent.deliverable.deliverableType?.name || "-",
      instruction: props.calendarEvent.deliverable.talentInstructions,
      contentTitle: props.calendarEvent.deliverable.contentTitle,
      goToDeal
    };
  },
});
</script>
<style scoped>
/* Content Wrapper */
.content-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 0;
}

.scrollable-content {
  flex: 1;
  padding: 10px;
}

/* Info Section */
.info-section {
  margin-bottom: 24px;
}

.company-info {
  display: flex;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid var(--loopin-color-gs-30);
}

.social-info {
  display: flex;
  align-items: center;
  padding: 16px 0;
  border-bottom: none;
  margin-top: 8px;
}

.company-logo, .social-logo {
  width: 48px;
  height: 48px;
  margin-right: 16px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.company-logo img, .social-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.company-logo-placeholder {
  width: 100%;
  height: 100%;
  background: var(--loopin-color-gs-30);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--loopin-color-gs-60);
  font-size: 20px;
}

.company-details, .social-details {
  flex: 1;
}

.company-name {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px 0;
  line-height: 1.3;
  color: var(--loopin-color-gs-100);
}

.social-name {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px 0;
  line-height: 1.3;
  color: var(--loopin-color-gs-90);
}

.company-subtitle, .social-subtitle {
  font-size: 14px;
  color: var(--loopin-color-gs-60);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Status Section */
.status-section {
  margin-bottom: 24px;
}

.status-card {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-radius: 12px;
  background: var(--loopin-item-background);
  border-left: 4px solid var(--loopin-color-gs-40);
  transition: all 0.2s ease;
}

.status-card--pink {
  border-left-color: var(--loopin-color-pink);
  background: linear-gradient(135deg, rgba(255, 105, 180, 0.1), var(--loopin-item-background));
}

.status-card--red {
  border-left-color: var(--loopin-color-red);
  background: linear-gradient(135deg, rgba(255, 0, 0, 0.1), var(--loopin-item-background));
}

.status-card--orange {
  border-left-color: var(--loopin-color-orange);
  background: linear-gradient(135deg, rgba(255, 165, 0, 0.1), var(--loopin-item-background));
}

.status-card--green {
  border-left-color: var(--loopin-color-green);
  background: linear-gradient(135deg, rgba(0, 128, 0, 0.1), var(--loopin-item-background));
}

.status-card--blue {
  border-left-color: var(--loopin-color-blue);
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.1), var(--loopin-item-background));
}

.status-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--loopin-color-gs-20);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  font-size: 16px;
  color: var(--loopin-color-gs-70);
}

.status-card--pink .status-icon {
  background: rgba(255, 105, 180, 0.2);
  color: var(--loopin-color-pink);
}

.status-card--red .status-icon {
  background: rgba(255, 0, 0, 0.2);
  color: var(--loopin-color-red);
}

.status-card--orange .status-icon {
  background: rgba(255, 165, 0, 0.2);
  color: var(--loopin-color-orange);
}

.status-card--green .status-icon {
  background: rgba(0, 128, 0, 0.2);
  color: var(--loopin-color-green);
}

.status-card--blue .status-icon {
  background: rgba(0, 123, 255, 0.2);
  color: var(--loopin-color-blue);
}

.status-content {
  flex: 1;
}

.status-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 4px;
  color: var(--loopin-color-gs-100);
}

.status-subtitle {
  font-size: 14px;
  color: var(--loopin-color-gs-60);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-date {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.status-date-icon {
  color: var(--loopin-color-gs-60);
  font-size: 14px;
  margin-right: 8px;
}

.status-date-text {
  font-size: 14px;
  font-weight: 500;
  color: var(--loopin-color-gs-70);
}

/* Instructions Section */
.instructions-section {
  margin-bottom: 24px;
}

.instructions-card {
  background: var(--loopin-item-background);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--loopin-color-gs-30);
}

.content-title, .instructions-content {
  margin-bottom: 20px;
}

.content-title:last-child, .instructions-content:last-child {
  margin-bottom: 0;
}

.content-title h4, .instructions-content h4 {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 12px 0;
  color: var(--loopin-color-gs-90);
}

.content-title p {
  font-size: 15px;
  line-height: 1.5;
  color: var(--loopin-color-gs-70);
  margin: 0;
}

.instructions-text {
  font-size: 15px;
  line-height: 1.6;
  color: var(--loopin-color-gs-80);
  white-space: pre-line;
}

.instructions-text a {
  color: var(--loopin-color-blue);
  text-decoration: none;
  font-weight: 500;
}

.instructions-text a:hover {
  text-decoration: underline;
}

/* Action Section */
.action-section {
  padding: 20px;
}

.primary-action {
  --background: var(--loopin-color-blue);
  --background-activated: var(--loopin-color-blue);
  --color: white;
  --border-radius: 12px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
  transition: all 0.2s ease;
}

.primary-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 123, 255, 0.4);
}

.button-icon {
  margin-right: 8px;
  font-size: 14px;
}

/* Modal Content */
.modal-content {
  --background: var(--loopin-color-gs-10);
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .company-name {
    font-size: 18px;
  }
  
  .meta-card, .instructions-card {
    padding: 16px;
  }
  
  .action-section {
    padding: 16px 10px;
  }
}
</style>